/**
 * CoverageHeatmapCell Component
 *
 * Individual cell in the coverage heatmap with hover details
 */

import { useState } from 'react';
import { Box, Popover, Typography, Divider } from '@mui/material';
import CapacityDetailsDialog from './CapacityDetailsDialog';
import { ProviderCapacityDay } from '../types/capacity.types';
import { RoleCapacityDayExtended, CoverageMetricType } from '../types/metrics.types';
import { formatMinutes } from '../utils/formatters';
import { getCellBackgroundColor, getSurplusColor, getVisitCapacityBackgroundColor, getVisitCapacityColor } from '../utils/colorHelpers';

interface CoverageHeatmapCellProps {
  roleDay: RoleCapacityDayExtended;
  providerDetails: ProviderCapacityDay[];
  metricType: CoverageMetricType;
  cellWidth?: number;
}

export default function CoverageHeatmapCell({
  roleDay,
  providerDetails,
  metricType,
  cellWidth = 80,
}: CoverageHeatmapCellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleClick = () => {
    setDialogOpen(true);
    handlePopoverClose(); // Close popover when opening dialog
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const open = Boolean(anchorEl);

  // Use different color logic based on metric type
  const backgroundColor = metricType === 'visitCapacity'
    ? getVisitCapacityBackgroundColor(roleDay.visitCapacity)
    : getCellBackgroundColor(roleDay.surplusPercent);

  const borderColor = metricType === 'visitCapacity'
    ? getVisitCapacityColor(roleDay.visitCapacity)
    : getSurplusColor(roleDay.surplusPercent);

  const isCompact = cellWidth < 55;

  // Determine what to display based on metric type
  const getDisplayValue = () => {
    switch (metricType) {
      case 'totalCapacity':
        // Show 0 if capacity is 0 or negative, otherwise show the value without + sign
        const capacityMinutes = Math.max(0, roleDay.gapMinutes);
        return formatMinutes(capacityMinutes);
      case 'avgContinuousTime':
        return formatMinutes(roleDay.avgContinuousMinutes);
      case 'visitCapacity':
        return `${roleDay.visitCapacity}`;
      default:
        return 'N/A';
    }
  };

  const getDisplayLabel = () => {
    switch (metricType) {
      case 'totalCapacity':
        return 'capacity';
      case 'avgContinuousTime':
        return isCompact ? 'avg' : 'avg block';
      case 'visitCapacity':
        return 'visits';
      default:
        return '';
    }
  };

  return (
    <>
      <Box
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isCompact ? 48 : 60,
          height: '100%',
          width: '100%',
          backgroundColor,
          border: `2px solid ${borderColor}`,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: 2,
          },
        }}
      >
        <Box sx={{ textAlign: 'center', px: 0.5, width: '100%' }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              fontSize: isCompact ? '0.75rem' : '0.875rem',
              lineHeight: 1.2,
            }}
          >
            {getDisplayValue()}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: isCompact ? '0.55rem' : '0.65rem',
              lineHeight: 1.2,
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {getDisplayLabel()}
          </Typography>
        </Box>
      </Box>

      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            {roleDay.role} - {new Date(roleDay.date).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total Shift Time:
            </Typography>
            <Typography variant="body2">
              {formatMinutes(roleDay.totalShiftMinutes)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total Visit Time:
            </Typography>
            <Typography variant="body2">
              {formatMinutes(roleDay.totalVisitMinutes)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Available Capacity:
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={roleDay.gapMinutes < 0 ? 'error' : 'success.main'}>
              {formatMinutes(Math.max(0, roleDay.gapMinutes))}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Avg Continuous:
            </Typography>
            <Typography variant="body2">
              {formatMinutes(roleDay.avgContinuousMinutes)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Visit Capacity:
            </Typography>
            <Typography variant="body2">
              {roleDay.visitCapacity} visits
            </Typography>
          </Box>

          {providerDetails.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Providers:
              </Typography>
              {providerDetails.map((provider) => (
                <Box key={provider.providerId} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption">
                    {provider.providerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {provider.utilization}%
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Popover>

      {/* Details Dialog */}
      <CapacityDetailsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        roleDay={roleDay}
        providerDetails={providerDetails}
        metricType={metricType}
      />
    </>
  );
}
